import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { fetchSportsbookAnalyticsKpis } from "./fetchSportsbookAnalyticsKpis";
import { filterToken } from "./sportsbookQueryUtils";
import type {
  SportsbookKpisQueryParams,
  SportsbookKpisResult,
} from "./sportsbookAnalyticsKpisTypes";

export const SPORTSBOOK_ANALYTICS_KPIS_QUERY_KEY =
  "SPORTSBOOK_ANALYTICS_KPIS" as const;

/**
 * Loads the aggregate KPIs for the reporting summary page. The query key
 * incorporates the full filter surface so the cache invalidates whenever the
 * user changes the applied date range or any dropdown. Mirrors the staleness /
 * focus behaviour of `useSportsbookAnalyticsSummary`.
 */
export function useSportsbookAnalyticsKpis(params: SportsbookKpisQueryParams) {
  const {
    start_date,
    end_date,
    money_type,
    bet_status,
    bet_type,
    sport,
    league,
    odds_provider,
    state,
  } = params;

  const query = useQuery<SportsbookKpisResult>({
    queryKey: [
      SPORTSBOOK_ANALYTICS_KPIS_QUERY_KEY,
      start_date,
      end_date,
      filterToken(money_type),
      filterToken(bet_status),
      filterToken(bet_type),
      filterToken(sport),
      filterToken(league),
      filterToken(odds_provider),
      filterToken(state),
    ],
    queryFn: () =>
      fetchSportsbookAnalyticsKpis({
        start_date,
        end_date,
        money_type,
        bet_status,
        bet_type,
        sport,
        league,
        odds_provider,
        state,
      }),
    enabled: Boolean(start_date && end_date),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    turnover: query.data?.turnover ?? 0,
    pnl: query.data?.pnl ?? 0,
    margin: query.data?.margin ?? 0,
    wasRangeClamped: query.data?.wasRangeClamped ?? false,
  };
}
